import {PolymerElement, html} from '@polymer/polymer';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {DhtmlxschedulermaterialSharedStylesHTML} from '../shared-styles/dhtmlxscheduler_material.js';
import {isSameDay, addDays, isBefore, isAfter, startOfDay, endOfDay} from 'date-fns'
import {ResizeObserverBehavior} from '../behaviors/resize-observer-behavior.js';
import {parseDateAndTime} from '@beauwest/business-date-parser';
import {backgroundColorValue, desiredTextColor} from '../../javascripts/modules/colors.js';

class KeliCalendar extends ScopeBinding(ResizeObserverBehavior(PolymerElement)) {
  static get template() {
    return html`
${DhtmlxschedulermaterialSharedStylesHTML}
<style>
  :host {
    display: block;
    min-height: 130px;
    height: 500px;
  }

  #schedulerContainer {
    width: 100%;
    height: 100%;
  }

  keli-prompt {
    top: 0 !important;
  }
  
  .dhx_cal_data div .dhx_cal_event {
    overflow: hidden;
  }
  
  .dhx_cal_event div.dhx_event_resize.dhx_footer {
    background-color: transparent !important;
  }
</style>

<keli-calendar-controller id="controller" keliname="_t"></keli-calendar-controller>

<div id="schedulerContainer" class="dhx_cal_container">
  <div class="dhx_cal_navline">
    <div class="dhx_cal_prev_button">&nbsp;</div>
    <div class="dhx_cal_next_button">&nbsp;</div>
    <div class="dhx_cal_today_button"></div>
    <div class="dhx_cal_date"></div>
    <div class="dhx_cal_tab" name="day_tab" style="right:204px;"></div>
    <div class="dhx_cal_tab" name="week_tab" style="right:140px;"></div>
    <div class="dhx_cal_tab" name="month_tab" style="right:76px;"></div>
  </div>
  <div class="dhx_cal_header"></div>
  <div class="dhx_cal_data"></div>
</div>`;
  }

  static get is() {
    return 'keli-calendar';
  }

  static get properties() {
    return {
      events: {type: Array, scopevar: true, observer: 'eventsChanged'},
      height: {type: Number, scopevar: true, observer: 'heightChanged'},
      isBlockPanelElement: {type: Boolean, value: true},
      keliControlTypes: {type: Array, value: ['calendar']},
      manualDialog: {type: Boolean, scopevar: true},
      manualNavigation: {type: Boolean, scopevar: true, observer: 'manualNavigationChanged'},
      ro: {type: Boolean, scopevar: true, observer: 'roChanged'},
      templates: {type: Object, scopevar: true, observer: 'applyCustomTemplates'},
      timeline: {type: Object, scopevar: true, observer: 'timelineChanged'},
      timespan: {type: Object, scopevar: true, observer: 'timespanChanged'},
      view: {type: String, scopevar: true, observer: 'viewChanged'}
    }
  }

  constructor() {
    super();
    this.addEmitToKeli(['keliCalendarViewChanged', 'keliCalendarEventCreated', 'keliCalendarEventChanged', 'keliCalendarEventDeleted', 'keliCalendarActivateEvent']);
  }

  connectedCallback() {
    super.connectedCallback();

    this.dispatchEvent(new CustomEvent('panelElementCreated', {
      bubbles: true,
      composed: true,
      detail: {element: this}
    }));

    this.scheduleInstance = Scheduler.getSchedulerInstance();
    this.scheduleInstance.skin = 'material';
    this.scheduleInstance.config.container_autoresize = false;
    this.scheduleInstance.config.start_on_monday = false;
    this.scheduleInstance.config.event_duration = 60;
    this.scheduleInstance.config.auto_end_date = true;
    this.scheduleInstance.config.left_border = false;
    this.scheduleInstance.config.show_quick_info = false;
    this.scheduleInstance.config.details_on_create = true;
    this.scheduleInstance.config.details_on_dblclick = true;
    this.scheduleInstance.config.mark_now = true;
    this.scheduleInstance.config.select = false;
    this.scheduleInstance.config.hour_date = '%g:%i %A';
    this.scheduleInstance.config.cascade_event_display = true;
    this.scheduleInstance.config.cascade_event_margin = 20;

    this.scheduleInstance.init(this.$.schedulerContainer, undefined, 'month');
    afterNextRender(this.$.schedulerContainer, () => {
      this.scheduleInstance.updateView();
    });

    this.scheduleInstance.showLightbox = id => {

      if (this.scheduleInstance._new_event) {
        this.scheduleInstance._new_event = null;
      }

      const event = this.scheduleInstance.getEvent(id);
      const inserting = this.scheduleInstance.getUserData(id, 'inserting');

      id = this.eventUUID(id);
      if (this.manualDialog) {
        this.dispatchEvent(new CustomEvent('keliCalendarActivateEvent', {
          detail: {
            id,
            event,
            inserting,
            _preserveKeys: ['event']
          }
        }));
      } else {
        this.showEventDialog(id, inserting);
      }
    };

    this.scheduleInstance.attachEvent('onSchedulerReady', () => {
      this.updateNowLineInterval = window.setInterval(() => {
        this.scheduleInstance.updateView();
      }, 1000 * 60 * 5)
    });

    this.scheduleInstance.attachEvent('onTemplatesReady', () => {
      this.scheduleInstance.templates.event_bar_date = start => ` <strong>${this.scheduleInstance.templates.event_date(start)}</strong> `;
      this.scheduleInstance.templates.event_header = (start, end) => `${this.scheduleInstance.templates.event_date(start)}–${this.scheduleInstance.templates.event_date(end)}`;
      if (this.templates) {
        this.applyCustomTemplates(this.templates);
      }
    });

    this.scheduleInstance.attachEvent('onViewChange', () => {
      let state = this.scheduleInstance.getState();
      this.dispatchEvent(new CustomEvent('keliCalendarViewChanged', {
        detail: {
          start: state.min_date,
          end: state.max_date
        }
      }));
    });

    this.scheduleInstance.attachEvent('onEventCreated', (id, event) => {
      if (typeof id === 'number') {
        this.scheduleInstance.setUserData(id, 'inserting', true);
      }

      id = this.eventUUID(id);
      this.dispatchEvent(new CustomEvent('keliCalendarEventCreated', {detail: {id, event, _preserveKeys: ['event']}}));
    });

    this.scheduleInstance.attachEvent('onEventChanged', (id, event) => {
      this.dispatchEvent(new CustomEvent('keliCalendarEventChanged', {detail: {id, event, _preserveKeys: ['event']}}));
    });

    this.scheduleInstance.attachEvent('onBeforeEventDelete', (id, event) => {
      this.dispatchEvent(new CustomEvent('keliCalendarEventDeleted', {detail: {id, event, _preserveKeys: ['event']}}));
      return true;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.updateNowLineInterval) {
      window.clearInterval(this.updateNowLineInterval);
    }
  }

  async keliReady(scope) {
    super.keliReady && await super.keliReady(scope);
    await this.$.controller.elementReady(this);
  }

  eventUUID(id) {
    if (typeof id === 'number') {
      const storedId = this.scheduleInstance.getUserData(id, '_newId');
      if (storedId) {
        return storedId;
      }

      let newId = (window.uuid && window.uuid.v4 && window.uuid.v4());
      this.scheduleInstance.setUserData(id, '_newId', newId);

      // Change the ID on a nextRender so that consecutive events work normally
      afterNextRender(this, () => {
        this.scheduleInstance.changeEventId(id, newId);
      });

      return newId;
    }
    return id;
  }

  handleElementResized() {
    if (this.scheduleInstance) {
      this.scheduleInstance.updateView();
    }
  }

  viewChanged(newValue) {
    let date = undefined;
    let view = newValue;
    if (typeof newValue === 'object') {
      date = (newValue.date ? parseDateAndTime(newValue.date) : date);
      view = newValue.name || name;
    }
    if (this.scheduleInstance && view) {
      if (this.scheduleInstance.isViewExists(view)) {
        this.scheduleInstance.setCurrentView(date, view);
      }
    }
  }

  eventsChanged(newValue) {
    if (newValue && newValue.length) {
      for (let i = 0; i < newValue.length; ++i) {
        if (newValue[i].start_date && typeof newValue[i].start_date !== 'number') {
          newValue[i].start_date = parseDateAndTime(newValue[i].start_date);
        }
        if (newValue[i].end_date && typeof newValue[i].end_date !== 'number') {
          newValue[i].end_date = parseDateAndTime(newValue[i].end_date);
        }
        this.formatEventColor(newValue[i]);
      }
    }
    this.scheduleInstance.clearAll();
    this.scheduleInstance.parse(newValue, 'json');
    this.scheduleInstance.updateView();
  }

  applyCustomTemplates(templates) {
    if (templates) {
      for (const name in templates) {
        if (!templates.hasOwnProperty(name)) {
          continue;
        }
        this.scheduleInstance.templates[name] = templates[name];
      }
      this.scheduleInstance.updateView();
    }
  }

  formatEventColor(event) {
    if (event && event.color) {
      event.color = backgroundColorValue(event.color);
      event.textColor = desiredTextColor(event.color, true);
    }
    return event;
  }

  manualNavigationChanged(newValue) {
    if (newValue) {
      this.$.schedulerContainer.querySelector('.dhx_cal_navline').style.display = 'none';
      this.scheduleInstance.xy.nav_height = -1;
    } else {
      this.$.schedulerContainer.querySelector('.dhx_cal_navline').style.display = '';
      this.scheduleInstance.xy.nav_height = this.scheduleInstance._skin_xy.nav_height[0];
    }
    this.scheduleInstance.updateView()
  }

  timelineChanged(newValue) {
    if (Array.isArray(newValue)) {
      newValue.forEach(function (timeline) {
        this.createNewTimeline(timeline);
      }, this);
    } else {
      this.createNewTimeline(newValue);
    }

    if (this.view) {
      this.viewChanged(this.view);
    }
  }

  timespanChanged(newValue) {
    if (Array.isArray(newValue)) {
      newValue.forEach(function (timespan) {
        this.createNewTimespan(timespan);
      }, this);
    } else {
      this.createNewTimespan(newValue);
    }
    this.scheduleInstance.updateView();
  }

  createNewTimeline(options) {
    const defaultOptions = {
      name: 'timeline',
      y_unit: [],
      y_property: 'section_id',
      x_date: '%g:%i %A'
    };
    Object.assign(defaultOptions, options);

    this.scheduleInstance.locale.labels[`${defaultOptions.name}_tab`] = defaultOptions.label || defaultOptions.name;
    const button = document.createElement('div');
    button.classList.add('dhx_cal_tab');
    button.setAttribute('name', `${defaultOptions.name}_tab`);
    this.$.schedulerContainer.querySelector('.dhx_cal_navline').appendChild(button);

    this.scheduleInstance.createTimelineView(defaultOptions);
    if (this.scheduleInstance._obj) {
      this.scheduleInstance.unset_actions();
      this.scheduleInstance._els = [];
    }
    this.scheduleInstance.get_elements();
    this.scheduleInstance.callEvent('onTemplatesReady', []);
    this.scheduleInstance.set_actions();
  }

  createNewTimespan(options) {
    const defaultOptions = {
      css: 'marked_default'
    };
    Object.assign(defaultOptions, options);

    if (defaultOptions.start_date) {
      defaultOptions.start_date = parseDateAndTime(defaultOptions.start_date);
    }
    if (defaultOptions.end_date) {
      defaultOptions.end_date = parseDateAndTime(defaultOptions.end_date);
    }

    this.scheduleInstance.addMarkedTimespan(defaultOptions);
  }

  heightChanged(newValue) {
    this.style.height = this.style.maxHeight = '';
    if (newValue) {
      if (newValue === 'auto') {
        this.style.height = 'auto';
        this.style.maxHeight = 'none';
        this.scheduleInstance.config.container_autoresize = true;
        this.scheduleInstance.updateView()
      } else {
        this.style.height = this.style.maxHeight = `${parseInt(newValue, 10)}px`;
      }
    }
  }

  roChanged(newValue) {
    this.scheduleInstance.config.readonly = newValue;
  }

  showEventDialog(eventId, inserting) {
    const event = this.scheduleInstance.getEvent(eventId);

    const newPrompt = document.createElement('keli-prompt');
    this.shadowRoot.appendChild(newPrompt);
    newPrompt.$.controller.populatePrompt({
      options: {
        title: `Edit: ${event.text}`,
        // @formatter:off
          elements: [
            {name: 'title', controltype: 'input', attributes: {caption: 'Event title', displayvalue: event.text, required: true}},
            {name: 'start_date', controltype: 'date', attributes: {caption: 'Start date', displayvalue: event.start_date, required: true}},
            {name: 'start_time', controltype: 'time', attributes: {caption: 'Start time', displayvalue: event.start_date}},
            {name: 'end_date', controltype: 'date', attributes: {caption: 'End date', displayvalue: event.end_date}},
            {name: 'end_time', controltype: 'time', attributes: {caption: 'End time', displayvalue: event.end_date}},
            {name: 'all_day', controltype: 'boolean', attributes: {caption: 'All day event'}},
            {name: 'deletebutton', controltype: 'button', region: 'actions', attributes: {caption: 'Delete'}},
            {name: 'cancelbutton', controltype: 'button', region: 'actions', attributes: {caption: 'Cancel'}},
            {name: 'savebutton', controltype: 'button', region: 'actions', attributes: {caption: 'Save'}, defaultaction: true}
            // @formatter:on
          ],
        callback: (error, response) => {
          this.scheduleInstance.setUserData(event.id, 'inserting', false);

          if (!response || response.cancelbutton) {
            if (inserting) {
              this.scheduleInstance.deleteEvent(event.id);
            }
            return;
          }
          if (response.deletebutton) {
            this.scheduleInstance.deleteEvent(event.id);
            return;
          }

          if (response.title) {
            event.text = response.title;
          }

          let startDate = startOfDay(parseDateAndTime(response.start_date || new Date()));
          let endDate = startOfDay(parseDateAndTime(response.end_date || response.start_date || new Date()));
          let startTime;
          let endTime;

          if (response.all_day) {
            if (isSameDay(startDate, endDate)) {
              addDays(endDate, 1);
            } else if (isAfter(endDate, endOfDay(startDate)) && endDate.getHours() === 0 && endDate.getMinutes() === 0) {
              addDays(endDate, 1);
            }
          } else {
            startTime = parseDateAndTime(response.start_time || startDate);
            endTime = parseDateAndTime(response.end_time || endDate);
          }

          if (startTime) {
            startDate.setHours(startTime.getHours());
            startDate.setMinutes(startTime.getMinutes());
          }
          if (endTime) {
            endDate.setHours(endTime.getHours());
            endDate.setMinutes(endTime.getMinutes());
          }
          if (isBefore(endDate, startDate)) {
            endDate = new Date(startDate.getTime());
          }

          event.start_date = startDate;
          event.end_date = endDate;
          this.scheduleInstance.addEvent(event);
        }
      }
    }, newPrompt);
  }

  loadExportScript(callback) {
    if (typeof this.scheduleInstance.exportToPDF !== 'undefined') {
      callback();
      return;
    }

    JavaScript.load('https://export.dhtmlx.com/scheduler/api.js', error => {
      if (error) {
        keliUILogger(error);
        return;
      }

      for (let i = 0; i < Scheduler._schedulerPlugins.length; ++i) {
        if (Scheduler._schedulerPlugins[i].name === 'add_export_methods') {
          Scheduler._schedulerPlugins[i](this.scheduleInstance);
        }
      }
      callback();
    });
  }

  exportToPDF(options) {
    this.loadExportScript(() => {
      this.scheduleInstance.exportToPDF(options);
    });
  }

  exportToPNG(options) {
    this.loadExportScript(() => {
      this.scheduleInstance.exportToPNG(options);
    });
  }

  exportToExcel(options) {
    this.loadExportScript(() => {
      this.scheduleInstance.exportToExcel(options);
    });
  }
}

customElements.define(KeliCalendar.is, KeliCalendar);
export {KeliCalendar};
